import { createRoot } from "react-dom/client";
import "./instrument";
import App from "./App";
import * as Sentry from "@sentry/react";
import { initBugReportContext } from "./lib/bugReportContext";
import { startAppVersionWatcher } from "./lib/appVersion";
import "./index.css";

// Start capturing console/network context for bug reports
initBugReportContext();
// Production only: notice deploys (/version.json) and recover from missing chunks (DEV-321)
startAppVersionWatcher();

// event.error is null when the event isn't a JS exception — a resource failed to
// load (event.target is the element; capture:true because those don't bubble), or
// a cross-origin script threw. Log the fields that identify which.
// Benign and unfixable from app code: fires when a ResizeObserver callback
// causes another resize in the same frame. Chrome reports it through onerror
// with error:null, so the dev overlay turns it into "(unknown runtime error)".
// Swallowed here (capture phase, before the overlay's own listener).
const IGNORED_ERRORS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

window.addEventListener(
  'error',
  (event) => {
    if (IGNORED_ERRORS.some((msg) => event.message?.includes(msg))) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }
    const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
    if (target && target !== (window as unknown as EventTarget) && target.tagName) {
      console.error(
        `Resource failed to load: <${target.tagName.toLowerCase()}> ${target.src ?? target.href ?? '(no url)'}`,
        target,
      );
      return;
    }
    console.error('Global error caught:', {
      message: event.message,
      source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
      error: event.error,
    });
  },
  true, // capture — resource load errors don't bubble
);

window.addEventListener('unhandledrejection', (event) => {
  const reason: any = event.reason;
  console.error('Unhandled promise rejection:', reason, {
    // A non-Error reason is what makes the dev overlay say "(unknown runtime error)".
    type: reason === null ? 'null' : typeof reason,
    constructor: reason?.constructor?.name,
    keys: reason && typeof reason === 'object' ? Object.keys(reason) : undefined,
  });
});

const container = document.getElementById("root")!;
// onUncaughtError/onCaughtError are React 19 RootOptions; we're on react-dom 18.3,
// whose types (and runtime) only know onRecoverableError. Kept for the upgrade,
// cast so the build type-checks until then.
createRoot(container, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
} as Parameters<typeof createRoot>[1]).render(<App />);
